import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../ui';

/**
 * Paginacion (estilo Stripe)
 *
 * Decisión cerrada del proyecto: paginación tipo Stripe — sin lista
 * infinita de números de página, solo "Anterior / Siguiente" + rango
 * "mostrando X–Y de Z" y selector de tamaño de página.
 *
 * Props:
 *   page, pageSize, total
 *   onPageChange: (nextPage) => void
 *   onPageSizeChange?: (size) => void
 *   pageSizeOptions?: number[]   default [25, 50, 100]
 */

export default function Paginacion({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const btn =
    'inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border transition-colors';

  return (
    <div className="flex items-center justify-between gap-4 px-1 py-3 text-xs text-slate-500">
      <div className="flex items-center gap-3">
        <span>
          {total === 0 ? (
            'Sin resultados'
          ) : (
            <>
              Mostrando <span className="font-medium text-slate-700">{from}</span>
              –<span className="font-medium text-slate-700">{to}</span> de{' '}
              <span className="font-medium text-slate-700">{total}</span>
            </>
          )}
        </span>

        {onPageSizeChange && (
          <span className="flex items-center gap-1.5">
            ·
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="border border-slate-200 rounded px-1.5 py-1 bg-white text-xs"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            por página
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!canPrev}
          onClick={() => canPrev && onPageChange(page - 1)}
          className={cn(
            btn,
            canPrev
              ? 'border-slate-200 text-slate-700 hover:bg-slate-50'
              : 'border-slate-100 text-slate-300 cursor-not-allowed'
          )}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Anterior
        </button>
        <span className="text-slate-400">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          disabled={!canNext}
          onClick={() => canNext && onPageChange(page + 1)}
          className={cn(
            btn,
            canNext
              ? 'border-slate-200 text-slate-700 hover:bg-slate-50'
              : 'border-slate-100 text-slate-300 cursor-not-allowed'
          )}
        >
          Siguiente
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
